import {
  addDefaultProps,
  addDisplayName,
  addHandlers,
  addLayoutEffect,
  addProps,
  addState,
  addWrapper,
  flowMax,
} from 'ad-hok'
import cx from 'classnames'
import React, { FC } from 'react'
import { Trans } from 'react-i18next'
import { createUseStyles } from 'react-jss'
import { Link } from 'react-router-dom'
import { addApi } from 'shared/src/api'
import {
  addLocation,
  addNavigate,
  addPathParam,
} from 'shared/src/utils/addNavigate'
import { addNavigationState } from 'shared/src/utils/addNavigationState'
import { trackBehindTheGateView } from 'shared/src/utils/analytics'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'
import { scrollToId } from 'shared/src/utils/scrollToId'
import { typedAs } from 'shared/src/utils/typedAs'

import { EditsGrid } from 'components/edits/EditsGrid'
import { Register } from 'components/register'
import { addLoadingAndErrorIndicators } from 'utils/addLoadingAndErrorIndicators'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { ConstrainWidth } from 'utils/layout'
import { addShowRegisterContext } from 'utils/register'
import {
  makeCreatorProfileUrl,
  makeFluidCreatorUrlWithMediaHash,
} from 'utils/routes'
import { FLUID_CONTAINER_STYLE, serifFontFamily } from 'utils/styles'
import { setPageTitle } from 'utils/title'
import { PROFILE_HEADER_Z_INDEX } from 'utils/zIndex'
import { NavBar } from '../navbar'
import { GridFeed } from './GridFeed'
import { Header } from './Header'
import { Links } from './Links'

const useStyles = createUseStyles({
  container: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    height: '100dvh',
  },
  profileContainer: {
    position: 'relative',
    zIndex: PROFILE_HEADER_Z_INDEX,
    transitionProperty: 'opacity',
    transitionDuration: '0.15s',
    scrollSnapAlign: 'start',
  },
  viewTabContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 18,
    marginBottom: 19,
  },
  slash: {
    fontFamily: serifFontFamily,
    fontSize: 20,
    letterSpacing: 0.01,
    color: '#fff',
    opacity: 0.4,
    paddingLeft: 12,
    paddingRight: 12,
  },
  viewTab: {
    fontFamily: serifFontFamily,
    fontSize: 20,
    letterSpacing: 0.01,
    color: '#fff',
    opacity: 0.4,
    textDecoration: 'none',
  },
  selectedViewTab: {
    color: '#fff',
    opacity: 1,
  },
  grayBackground: {
    background: '#2D2D2D',
    flex: 1,
  },
  italics: {
    fontStyle: 'italic',
  },
  wantMore: {
    scrollSnapAlign: 'end',
  },
  fluidContainer: {
    height: '100%',
    ...FLUID_CONTAINER_STYLE,
  },
})

interface ViewTabProps {
  viewName: ProfileViewType
  displayNameTransString: string
  currentView: string
  username: string
  onClick?: () => void
}

const ViewTab: FC<ViewTabProps> = flowMax(
  addDisplayName('ViewTab'),
  addDefaultProps({
    firstTab: false,
  }),
  addProps(({ viewName, currentView, username }) => ({
    isSelectedView: viewName === currentView,
    to: makeCreatorProfileUrl(username, viewName),
  })),
  addLocation,
  addTranslationHelpers,
  addProps(() => ({
    classes: useStyles({}),
  })),
  ({
    displayNameTransString,
    to,
    isSelectedView,
    onClick,
    location,
    classes,
    t,
  }) => (
    <>
      <Link
        to={to}
        className={cx(
          classes.viewTab,
          isSelectedView && classes.selectedViewTab
        )}
        replace
        onClick={onClick}
        state={location.state}
      >
        <Trans t={t} i18nKey={displayNameTransString as any}>
          <span className={classes.italics} />
        </Trans>
      </Link>
    </>
  )
)

export type ProfileViewType = 'posts' | 'edits' | 'links' | 'default'

interface Props {
  initialViewType: ProfileViewType
}

export const Profile: FC<Props> = flowMax(
  addDisplayName('Profile'),
  addPathParam('username'),
  setPageTitle(({ username }) => username),
  addWrapper((render, { username }) => (
    <React.Fragment key={username}>{render()}</React.Fragment>
  )),
  addApi,
  addShowRegisterContext,
  addDataLoadingFromPromise(
    'feed',
    ({ username, api }) => api.users.feed(username),
    ['username'],
    ({ username }) => `${username}-profile`
  ),
  addLoadingAndErrorIndicators('feed', {
    redirectTo404IfError: true,
  }),
  addState('viewType', 'setViewType', ({ feed: { user }, initialViewType }) =>
    typedAs<ProfileViewType>(
      initialViewType === 'default'
        ? user.defaultToLinksPage
          ? 'links'
          : 'posts'
        : initialViewType
    )
  ),
  addNavigationState('scrollTo', 'setScrollTo', typedAs<number | null>(null)),
  addNavigate,
  addLocation,
  addHandlers({
    onClickEdits:
      ({ setViewType, feed: { user }, api }) =>
      () => {
        trackBehindTheGateView(user.id, api)
        setViewType('edits')
      },
    onClickMedia:
      ({ navigate, username }) =>
      (mediaId: number) => {
        navigate(
          makeFluidCreatorUrlWithMediaHash({
            creatorUsername: username,
            mediaId,
          }),
          {
            state: {
              showBackButton: true,
            },
          }
        )
      },
  }),
  addLayoutEffect(
    ({ viewType, scrollTo, setScrollTo }) =>
      () => {
        if (viewType === 'posts' && scrollTo) {
          scrollToId(scrollTo?.toString())
          setScrollTo(null)
        }
      },
    ['mediaViewType']
  ),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    feed: { user },
    feed,
    username,
    showRegister,
    viewType,
    onClickEdits,
    onClickMedia,
    setViewType,
    classes,
  }) => (
    <ConstrainWidth fillHeight className={classes.container}>
      {showRegister ? (
        <Register />
      ) : (
        <>
          <NavBar shareCreatorId={user.id} showHomeButton={!user.isStealth} />
          <div className={classes.profileContainer}>
            <Header feed={feed} />
            <div className={classes.viewTabContainer}>
              <ViewTab
                viewName="posts"
                displayNameTransString={
                  user.showMerch ? 'profile.merch' : 'profile.myRodeo'
                }
                username={username}
                currentView={viewType}
                onClick={() => setViewType('posts')}
              />
              {user.showEdits && (
                <>
                  <span className={classes.slash}>/</span>
                  <ViewTab
                    viewName="edits"
                    displayNameTransString={'profile.edits'}
                    username={username}
                    currentView={viewType}
                    onClick={onClickEdits}
                  />
                </>
              )}
              <>
                <span className={classes.slash}>/</span>
                <ViewTab
                  viewName="links"
                  displayNameTransString={'profile.links'}
                  username={username}
                  currentView={viewType}
                  onClick={() => setViewType('links')}
                />
              </>
            </div>
          </div>
          {viewType === 'posts' && (
            <GridFeed feed={feed} onClick={onClickMedia} />
          )}
          {viewType === 'edits' && <EditsGrid />}
          {viewType === 'links' && (
            <div className={classes.grayBackground}>
              <Links feed={feed} onClickMyRodeo={() => setViewType('posts')} />
            </div>
          )}
        </>
      )}
    </ConstrainWidth>
  )
)
